import {
  AnalyticsPageName,
  BacktestingRunType,
  LanguageNameSpaces,
} from "@shared/types";
import { toFixed } from "@shared/utils/numbers";
import { PERCENT_PRECISION } from "@shared/features/tradingTerminal/common/constants";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { BacktestingGeneralStatistics } from "@shared/features/bots/BotForm/common/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface UseStatisticsProps {
  backtestingRun?: BacktestingRunType;
}

const useStatistics = ({ backtestingRun }: UseStatisticsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();

  const getValue = (statisticType: BacktestingGeneralStatistics) => {
    const runKey = STATISTIC_TYPE_TO_KEY[statisticType];
    if (!runKey) return 0;
    return toFixed(backtestingRun?.[runKey]?.[displayCurrency] || 0, precision);
  };

  const initialPortfolioSize = getValue(
    BacktestingGeneralStatistics.INITIAL_FUNDS
  );
  const realizedPnl = getValue(BacktestingGeneralStatistics.REALIZED_PNL);
  const unrealizedPnl = getValue(BacktestingGeneralStatistics.UNREALIZED_PNL);

  const titleToValue: Record<BacktestingGeneralStatistics, number> = {
    [BacktestingGeneralStatistics.INITIAL_FUNDS]: initialPortfolioSize,
    [BacktestingGeneralStatistics.REALIZED_PNL]: realizedPnl,
    [BacktestingGeneralStatistics.UNREALIZED_PNL]: unrealizedPnl,
    [BacktestingGeneralStatistics.NET_PNL]: realizedPnl + unrealizedPnl,
  };

  const getChangeValue = (statisticType: BacktestingGeneralStatistics) => {
    const value = titleToValue[statisticType];
    const divideBy =
      statisticType === BacktestingGeneralStatistics.UNREALIZED_PNL
        ? initialPortfolioSize + realizedPnl
        : initialPortfolioSize;
    return toFixed((value / divideBy) * 100 || 0, PERCENT_PRECISION);
  };

  const getDisplayedValue = (value: number) => {
    return value > 999 ? Math.round(value) : toFixed(value, precision);
  };

  const titleToHelperDescription = {
    [BacktestingGeneralStatistics.INITIAL_FUNDS]: t(
      "backtesting.helper_tooltips.statistics.initial_funds"
    ),
    [BacktestingGeneralStatistics.REALIZED_PNL]: t(
      "backtesting.helper_tooltips.statistics.realized_pnl"
    ),
    [BacktestingGeneralStatistics.UNREALIZED_PNL]: t(
      "backtesting.helper_tooltips.statistics.unrealized_pnl"
    ),
    [BacktestingGeneralStatistics.NET_PNL]: t(
      "backtesting.helper_tooltips.statistics.net_pnl"
    ),
  };

  const getHelperProps = (title: BacktestingGeneralStatistics) => {
    return {
      description: titleToHelperDescription[title],
      analyticsPageName: AnalyticsPageName.BACKTESTING,
      analyticsFieldName: title,
    };
  };

  return {
    titleToValue,
    getChangeValue,
    getDisplayedValue,
    getHelperProps,
  };
};

export default useStatistics;

const STATISTIC_TYPE_TO_KEY: {
  [key in BacktestingGeneralStatistics]?: keyof Pick<
    BacktestingRunType,
    "realised_pnl" | "unrealised_pnl" | "initial_portfolio_size"
  >;
} = {
  [BacktestingGeneralStatistics.REALIZED_PNL]: "realised_pnl",
  [BacktestingGeneralStatistics.UNREALIZED_PNL]: "unrealised_pnl",
  [BacktestingGeneralStatistics.INITIAL_FUNDS]: "initial_portfolio_size",
};
